
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { CompanyFormValues } from '@/components/company/CompanyFormSchema';
import { supabase } from '@/integrations/supabase/client';

export function useCompanyUpdate() {
  const [loading, setLoading] = useState<boolean>(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleUpdateCompany = async (data: CompanyFormValues, companyData: any, setCompanyData: (data: any) => void) => {
    setLoading(true);
    try {
      // Get company ID from metadata - check both possible naming conventions
      let companyId = user?.user_metadata?.company_id || user?.user_metadata?.companyId;
      
      // If still no company ID, use the one from companyData
      if (!companyId && companyData?.id) {
        companyId = companyData.id;
      }
      
      if (!companyId) {
        toast({
          title: "Unable to update company",
          description: "Company ID not found. Please contact support.",
          variant: "destructive"
        });
        return;
      }
      
      // Update company in Supabase
      const { error } = await supabase
        .from('companies')
        .update({
          locations: [data.location],
          bio: data.bio,
          staff_size: data.staffSize,
          website: data.website || null,
          twitter_url: data.twitterUrl || null, 
          instagram_url: data.instagramUrl || null,
          linkedin_url: data.linkedinUrl || null
        })
        .eq('id', companyId);
        
      if (error) {
        console.error("Error updating company:", error);
        toast({
          title: "Update failed",
          description: error.message,
          variant: "destructive"
        });
      } else {
        // Refresh company data
        const { data: refreshedCompany, error: fetchError } = await supabase
          .from('companies')
          .select('*')
          .eq('id', companyId)
          .single();
          
        if (!fetchError && refreshedCompany) {
          setCompanyData(refreshedCompany);
        }
        
        toast({
          title: "Company updated",
          description: "Your company information has been updated successfully.",
        });
        return true;
      }
    } catch (err) {
      console.error("Error in handleUpdateCompany:", err);
      toast({
        title: "Update failed",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
    return false;
  };

  return { handleUpdateCompany, loading };
}
